import {
  BellOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  MailOutlined,
  StopOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { Logo, join } from "@p0security/frontend-core/react";
import { ApprovalRule } from "@p0security/shared/types/policy/types";
import { hasApprovalOptions } from "@p0security/shared/types/policy/util";
import { Divider, Flex, Popover, Space, Typography } from "@p0security/ui";
import { sortBy } from "lodash-es";
import { ReactNode } from "react";
import { stopPropagation } from "utils/javascript";

import {
  ApprovalOptionsPreview,
  MaxDurationTag,
} from "../components/previews/ApprovalOptionsPreview";
import { ApprovalPreview } from "../components/previews/ApprovalPreview";

export const approvalTypeLabels: Record<ApprovalRule["type"], string> = {
  auto: "Auto-approve (on-call)",
  p0: "P0 Security Reviewers",
  group: "Group approval",
  deny: "Deny all",
  persistent: "Always allowed",
  escalation: "Escalation service",
  "requestor-profile": "Manager approval",
};

export function getApprovalIcon(type: ApprovalRule["type"]): ReactNode {
  switch (type) {
    case "auto":
      return <ClockCircleOutlined />;
    case "escalation":
      return <BellOutlined />;
    case "p0":
      return <Logo logo="/p0-dark-logomark.png" title="P0 Logomark" />;
    case "persistent":
      return (
        <CheckCircleOutlined
          /* eslint-disable-line react/forbid-component-props */ style={{
            color: "#52c41a",
          }}
        />
      );
    case "requestor-profile":
      return <MailOutlined />;
    case "group":
      return <TeamOutlined />;
    case "deny":
      return (
        <StopOutlined
          /* eslint-disable-line react/forbid-component-props */ style={{
            color: "#f5222d",
          }}
        />
      );
  }
}

function renderFullApprovalList(record: ApprovalRule[]): ReactNode {
  const rendered = sortBy(record, "type").map((item: ApprovalRule, index) => (
    <Flex vertical gap={8} key={`${item.type}-${index}`}>
      <div className="whitespace-nowrap">
        <ApprovalPreview approval={item} />
      </div>
      {hasApprovalOptions(item) && item.options && (
        <ApprovalOptionsPreview approvalOptions={item.options} />
      )}
      <MaxDurationTag approval={item} />
    </Flex>
  ));

  return join(rendered, (index) => (
    <Divider size="small" key={`divider-${index}`} />
  ));
}

export const ApprovalSummary = ({
  record,
}: {
  record: ApprovalRule[] | null | undefined;
}) => {
  if (!record || !Array.isArray(record) || record.length === 0) {
    return "No approvals";
  }

  if (record.length === 1) {
    return renderFullApprovalList(record);
  }

  return (
    <Popover content={renderFullApprovalList(record)} trigger="hover">
      <Space size={8} className="cursor-pointer" onClick={stopPropagation}>
        {sortBy(record, "type").map((item: ApprovalRule, index: number) => (
          <span
            key={index}
            style={{
              display: "inline-flex",
              alignItems: "center",
              width: 20,
              height: 20,
              fontSize: 16,
            }}
          >
            {getApprovalIcon(item.type)}
          </span>
        ))}
        <Typography.Text type="secondary" className="whitespace-nowrap">
          {record.length} actions configured
        </Typography.Text>
      </Space>
    </Popover>
  );
};
